import React, { useState } from 'react';
import Modal from 'react-modal';
import { FaTimes, FaUserMinus, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { reportsAPI } from '../services/api';

Modal.setAppElement('#root');

const BlockModal = ({ isOpen, onClose, userId, userName }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBlock = async () => {
    setIsSubmitting(true);

    try {
      await reportsAPI.blockUser({
        user_id: userId
      });

      toast.success(`${userName} has been blocked`);
      onClose();

      // Navigate back to messages list after blocking
      window.location.href = '/messages';
    } catch (error) {
      console.error('Block error:', error);
      toast.error('Failed to block user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-dialog modal-sm"
      overlayClassName="modal-overlay"
    >
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">
            <FaUserMinus className="text-red-500 mr-2" />
            Block {userName}
          </h4>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaUserMinus className="text-red-500 text-3xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Block {userName}?
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              This will prevent {userName} from contacting you. You won't be able to see their messages,
              and they won't be able to see yours. This action cannot be undone.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <FaExclamationTriangle className="text-amber-500 mt-0.5 mr-3 flex-shrink-0 text-lg" />
              <div>
                <p className="text-sm text-amber-800 font-medium mb-2">What happens when you block someone?</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 flex-shrink-0"></span>
                    They can't send you messages
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 flex-shrink-0"></span>
                    You won't see their messages
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 flex-shrink-0"></span>
                    Your conversation will be deleted
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 flex-shrink-0"></span>
                    They won't be notified
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-blue-500 mr-3 mt-0.5">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-blue-800 font-medium mb-1">Need to unblock?</p>
                <p className="text-sm text-blue-700">
                  If you change your mind, you can unblock users in your settings at any time.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleBlock}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Blocking...' : 'Block User'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BlockModal;
